import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { ChevronLeft } from 'lucide-react';
import { ComponentProps, useState } from 'react';
import { Button } from '../../../../design-system/button/button';
import { Inline } from '../../../../design-system/inline/inline';
import { Modal, ModalContent, ModalDescription, ModalTitle } from '../../../../design-system/modal/modal';
import { Stack } from '../../../../design-system/stack/stack';
import { CategoryView, VendorView } from '../../../../types/entity-types';
import { CreateCategoryForm } from './create-category-form';
import { SelectVendorCategory } from './select-vendor-category';

type ChangeVendorModalView = 'SELECT_CATEGORY' | 'CREATE_CATEGORY';

type ChangeVendorCategoryModalProps = {
  isOpen: boolean;
  vendor?: VendorView;
  categories: CategoryView[];
  isLoading: boolean;
  onClose: () => void;
  onChangeCategory: ComponentProps<typeof SelectVendorCategory>['onChangeCategory'];
  onCreateCategory: ComponentProps<typeof CreateCategoryForm>['onSubmit'];
};

export const ChangeVendorCategoryModal = ({
  isOpen,
  vendor,
  categories,
  isLoading,
  onClose,
  onChangeCategory,
  onCreateCategory,
}: ChangeVendorCategoryModalProps) => {
  const [view, setView] = useState<ChangeVendorModalView>('SELECT_CATEGORY');

  if (!vendor) {
    return null;
  }

  return (
    <Modal open={isOpen} onOpenChange={onClose}>
      <ModalContent className="h-[414-px] w-[520px] bg-white py-6">
        <VisuallyHidden>
          <ModalTitle>Change Vendor Category</ModalTitle>

          <ModalDescription>Modal to change the category of a vendor</ModalDescription>
        </VisuallyHidden>

        {view === 'SELECT_CATEGORY' && (
          <SelectVendorCategory
            selectedCategoryId={vendor.category?.id}
            vendorConfig={{
              name: vendor.name,
              logo: vendor.logo,
            }}
            categories={categories}
            isLoading={isLoading}
            onCreateNewCategory={() => setView('CREATE_CATEGORY')}
            onChangeCategory={onChangeCategory}
            onCancel={onClose}
          />
        )}
        {view === 'CREATE_CATEGORY' && (
          <Stack space={3} align="stretch" className="px-8">
            <Inline>
              <Button
                variant="secondary"
                size="iconLarge"
                aria-label="Back to select category"
                onClick={() => setView('SELECT_CATEGORY')}
              >
                <ChevronLeft size={24} />
              </Button>

              <h2 className="text-xl font-medium text-black">Create New Category</h2>
            </Inline>

            <CreateCategoryForm
              level1Category={vendor.category?.level1}
              categories={categories}
              isLoading={isLoading}
              onSubmit={onCreateCategory}
              onCancel={() => setView('SELECT_CATEGORY')}
            />
          </Stack>
        )}
      </ModalContent>
    </Modal>
  );
};
